import { bridgeDataReady } from '@databases/database-state/state/bridge-data';

import { transformToken } from '@canvas/frame-api/canvas-frame-api';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';
import { type FrameToken } from '@canvas/frame-api-types';
import { getPaywallCacheFeatureData, updatePaywallFeatureState } from '@canvas/paywall';
import { captureException } from '@canvas/sentry-react';

import {
  getLastKnownDatabasePaywallFeatureState,
  syncDatabasePaywallFeatureState,
} from './database-paywall-feature-state-cache.ts';

const syncPaywallFeatureState = (token: FrameToken | undefined): boolean => {
  if (token?.features) {
    syncDatabasePaywallFeatureState(token.features);
    return true;
  }

  return false;
};

const syncPaywallFeatureStateFromBridgeData = async (
  sync: typeof syncPaywallFeatureState = syncPaywallFeatureState,
) => {
  const bridgeData = await bridgeDataReady.get();

  sync(transformToken(bridgeData.token));
};

export const initializeDatabasePaywallFeatures = () => {
  let hasSyncedPaywallFeatureData = false;
  let hasAppliedLastKnownPaywallFeatureData = false;
  let hasResolvedBridgeData = false;
  let hasResolvedCachedFeatureData = false;

  const resolveWithLastKnownPaywallFeatureStateIfNeeded = () => {
    if (
      hasSyncedPaywallFeatureData ||
      hasAppliedLastKnownPaywallFeatureData ||
      !hasResolvedBridgeData
    ) {
      return;
    }

    const lastKnownPaywallFeatureState = getLastKnownDatabasePaywallFeatureState();

    if (!lastKnownPaywallFeatureState) {
      return;
    }

    hasAppliedLastKnownPaywallFeatureData = true;
    updatePaywallFeatureState(lastKnownPaywallFeatureState);
  };

  const resolveWithDefaultPaywallFeatureStateIfNeeded = () => {
    if (
      hasSyncedPaywallFeatureData ||
      hasAppliedLastKnownPaywallFeatureData ||
      !hasResolvedBridgeData ||
      !hasResolvedCachedFeatureData
    ) {
      return;
    }

    // Only fall back once the startup sources that may have paywall data have
    // resolved. If they are all missing features and there is no last-known
    // value for this tab, treat the user as locked via the empty default state.
    updatePaywallFeatureState({});
  };

  const sync = (token: FrameToken | undefined): boolean => {
    const didSync = syncPaywallFeatureState(token);

    if (didSync) {
      hasSyncedPaywallFeatureData = true;
    }

    return didSync;
  };

  const bridgePaywallFeatureStatePromise = syncPaywallFeatureStateFromBridgeData(
    sync,
  ).finally(() => {
    hasResolvedBridgeData = true;
    resolveWithLastKnownPaywallFeatureStateIfNeeded();
    resolveWithDefaultPaywallFeatureStateIfNeeded();
  });
  void bridgePaywallFeatureStatePromise;

  getPaywallCacheFeatureData()
    .then((data) => {
      if (hasSyncedPaywallFeatureData || data === undefined) {
        return;
      }

      hasSyncedPaywallFeatureData = true;
      // This data already came from the shared cache, so avoid writing it back.
      syncDatabasePaywallFeatureState(data, { shouldWritePaywallCache: false });
    })
    .catch((error) => {
      captureException(error, {
        tags: {
          captureLocation:
            'initialize-database-paywall-features-get-paywall-cache-feature-data',
        },
      });
    })
    .finally(() => {
      hasResolvedCachedFeatureData = true;
      resolveWithDefaultPaywallFeatureStateIfNeeded();
    });

  waitForContentAPI().then(async (contentAPI) => {
    contentAPI.onTokenInit(sync);
    contentAPI.onTokenChange(sync);

    try {
      const didSyncFromContentAPI = sync(await contentAPI.getToken());

      if (!didSyncFromContentAPI) {
        await bridgePaywallFeatureStatePromise;
      }
    } catch {
      await bridgePaywallFeatureStatePromise;
    }
  });
};
